import type {Business, User} from "@irati/types"
import {PERKS, RECOGNITION, MEDICAL, WALLET, PLATFORM, REWARD, ONBOARDING, TOUR, WELLNESS, HOMEPAGE, ONLINEGP} from "./products"

export const clickEventId = "cta_button_click"

interface UrlInfo {
  path: string
  search: string
}

interface UrlDelta {
  previous?: UrlInfo
  current: UrlInfo
}

export interface Tracker {
  trackUrl(urlDelta: UrlDelta, attrs: any): void
  trackEvent: (name: string, data: any) => void
  handleLogin: (loginDetails: ReturnType<Tracking["getLoginDetails"]>, data: any) => void
  handleLogout: () => void
}

/**
 * @description: Very simple key value store to fake localstorage on native.
 * Region stuff relies on sync get of current region, using native localstorage relies on async code.
 */
export interface TrackingStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  clear(): void
}

interface TrackerOpts {
  platform: Tracker
  user: User
  business: Business
  storage: TrackingStorage
}

class Tracking {
  private user?: User
  private business?: Business
  private platform?: Tracker
  private storage?: TrackingStorage

  trackLogin(opts: TrackerOpts) {
    // we only want to track logged in users as this time
    this.user = opts.user
    this.business = opts.business
    this.platform = opts.platform
    this.storage = opts.storage

    // NOTE: do this last to ensure everything is ready in the class
    this.platform.handleLogin(this.getLoginDetails(), this.getDefaultAttributes())
  }

  trackLogout() {
    this.platform?.handleLogout()
    this.user = undefined
    this.business = undefined
    this.platform = undefined
  }

  trackRecognitionEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: RECOGNITION
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackRewardEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: REWARD
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackOnlineGPEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: ONLINEGP
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackPerksEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: PERKS
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackMedicalEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: MEDICAL
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackWalletEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: WALLET
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackPlatformEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: PLATFORM
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackOnboardingEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: ONBOARDING
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackTourEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: TOUR
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackWellnessEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: WELLNESS
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackSearchEvent = (name: string, dataAttributes: any, isRewards = false) => {
    const productData = {
      perkbox_product: isRewards ? REWARD : PERKS
    }

    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackCTAEvent = (dataAttributes?: {source: string; type: string; perkbox_product: string}) => {
    if (!dataAttributes?.perkbox_product) {
      return
    }
    const eventData = {
      ...dataAttributes
    }
    this.trackEvent(clickEventId, {...eventData})
  }

  trackHomepageEvent = (name: string, dataAttributes?: any) => {
    const productData = {
      perkbox_product: HOMEPAGE
    }
    this.trackEvent(name, {...productData, ...dataAttributes})
  }

  trackUrl(urlDelta: UrlDelta) {
    return this.platform?.trackUrl(urlDelta, this.getDefaultAttributes())
  }

  getLoginDetails(detail?: string, type?: string, product?: string, email?: string, phone?: string) {
    return {
      detail: detail || this.getStorageItem("login_detail"),
      type: type || this.getStorageItem("login_type"),
      product: product || this.getStorageItem("login_product"),
      login_available: this.getStorageItem("login_available"),
      domain: !!this.getStorageItem("login_domain"),
      domain_name: this.getStorageItem("login_domain"),
      email,
      phone
    }
  }

  private getStorageItem(value: string) {
    return this.storage?.getItem(value)
  }

  private trackEvent(name: string, dataAttributes: any) {
    const eventDefaultAttributes = this.getDefaultAttributes()
    return this.platform?.trackEvent(name, {...eventDefaultAttributes, ...dataAttributes})
  }

  private getDefaultAttributes() {
    return {
      user_id: this.user?.uuid,
      tenant_locale: this.user?.regionId,
      tenant_id: this.user?.businessId,
      tenant_grouping: this.business?.groupIds,
      tenant_subdomain: this.business?.subdomain,
      tenant_public_name: this.business?.businessName,
      perkbox_platform: PLATFORM
    }
  }
}
export default new Tracking()
